import { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Badge, Box, Button, Group, Loader, ScrollArea, Select, Stack, Tabs, Text } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import {
  activatePromptVersion,
  listPromptVersions,
  type PromptVersion,
} from '@mygpt/app/apis/services/prompt-versions';
import { computeNumberedSideBySideDiff, type DiffSegment } from './diff-utils';

const CURRENT_EDIT_ID = '_current_edit';

interface VersionComparisonModalProps {
  opened: boolean;
  onClose: () => void;
  aiId: string;
  promptType: 'main' | 'realtime' | 'phone';
  versions: PromptVersion[];
  initialVersionAId: string | null;
  currentPrompt?: string;
  onVersionActivated: () => void;
}

export const VersionComparisonModal = ({
  opened,
  onClose,
  aiId,
  promptType,
  versions,
  initialVersionAId,
  currentPrompt,
  onVersionActivated,
}: VersionComparisonModalProps) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<string>(promptType);
  const [versionAId, setVersionAId] = useState<string | null>(initialVersionAId);
  const [versionBId, setVersionBId] = useState<string | null>(null);
  const [operating, setOperating] = useState(false);

  // 按 tab（prompt 类型）分别存储版本列表
  const [versionsByType, setVersionsByType] = useState<Record<string, PromptVersion[]>>({});
  const [loadingVersions, setLoadingVersions] = useState(false);

  // 初始化：将传入的 versions 放到对应 tab
  useEffect(() => {
    if (opened && versions.length > 0) {
      setVersionsByType((prev) => ({ ...prev, [promptType]: versions }));
    }
  }, [opened, versions, promptType]);

  // 当 initialVersionAId 变化时更新选择，默认将 Version A 设为前一个版本
  useEffect(() => {
    if (initialVersionAId && opened) {
      const clickedIndex = versions.findIndex((v) => v.id === initialVersionAId);
      if (clickedIndex >= 0 && clickedIndex < versions.length - 1) {
        // versions 按版本号降序排列（最新在前），下一个元素即为前一个版本
        setVersionAId(versions[clickedIndex + 1].id);
      } else {
        setVersionAId(initialVersionAId);
      }
      setVersionBId(CURRENT_EDIT_ID);
      setActiveTab(promptType);
    }
  }, [initialVersionAId, opened, versions, promptType]);

  // Tab 切换时加载另一类型的版本
  const handleTabChange = useCallback(
    async (tab: string | null) => {
      if (!tab) return;
      setActiveTab(tab);
      setVersionAId(null);
      setVersionBId(CURRENT_EDIT_ID);

      const targetVersions = versionsByType[tab] || [];
      if (targetVersions.length === 0) {
        setLoadingVersions(true);
        try {
          const data = await listPromptVersions(aiId, tab, 1, 50);
          setVersionsByType((prev) => ({ ...prev, [tab]: data.items }));
        } catch {
          // ignore
        } finally {
          setLoadingVersions(false);
        }
      }
    },
    [aiId, versionsByType]
  );

  // 当前 tab 的版本列表
  const currentVersions = versionsByType[activeTab] || [];

  // 获取 prompt 文本
  const getPromptText = (id: string | null): string => {
    if (!id) return '';
    if (id === CURRENT_EDIT_ID) return currentPrompt || '';
    return currentVersions.find((v) => v.id === id)?.prompt || '';
  };

  const promptA = getPromptText(versionAId);
  const promptB = getPromptText(versionBId);

  const diff = useMemo(() => {
    if (!versionAId || !versionBId) return null;
    return computeNumberedSideBySideDiff(promptA, promptB);
  }, [promptA, promptB, versionAId, versionBId]);

  // 下拉选项：虚拟「現在（編集中）」 + 历史版本
  const versionOptions = [
    { value: CURRENT_EDIT_ID, label: t('settings.prompt.current_unsaved') },
    ...currentVersions.map((v) => ({
      value: v.id,
      label: `v${v.version} - ${new Date(v.created_at).toLocaleString()}${
        v.is_active ? ` (${t('settings.prompt.active')})` : ''
      }`,
    })),
  ];

  const handleRestoreA = async () => {
    if (!versionAId || versionAId === CURRENT_EDIT_ID || operating) return;
    const version = currentVersions.find((v) => v.id === versionAId);
    if (!version || version.is_active) return;
    setOperating(true);
    try {
      await activatePromptVersion(aiId, version.id);
      showNotification({ message: t('settings.prompt.version_activated'), color: 'green' });
      onVersionActivated();
      onClose();
    } catch {
      showNotification({ message: t('settings.prompt.version_activate_error'), color: 'red' });
    } finally {
      setOperating(false);
    }
  };

  const handleRestoreB = async () => {
    if (!versionBId || versionBId === CURRENT_EDIT_ID || operating) return;
    const version = currentVersions.find((v) => v.id === versionBId);
    if (!version || version.is_active) return;
    setOperating(true);
    try {
      await activatePromptVersion(aiId, version.id);
      showNotification({ message: t('settings.prompt.version_activated'), color: 'green' });
      onVersionActivated();
      onClose();
    } catch {
      showNotification({ message: t('settings.prompt.version_activate_error'), color: 'red' });
    } finally {
      setOperating(false);
    }
  };

  const renderSegments = (segments: DiffSegment[]) => {
    return segments.map((seg, idx) => {
      if (seg.type === 'unchanged') return <span key={idx}>{seg.text}</span>;
      if (seg.type === 'removed') {
        return (
          <span key={idx} style={{ backgroundColor: '#fecaca', color: '#dc2626', textDecoration: 'line-through' }}>
            {seg.text}
          </span>
        );
      }
      return (
        <span key={idx} style={{ backgroundColor: '#bbf7d0', color: '#16a34a' }}>
          {seg.text}
        </span>
      );
    });
  };

  // AI 摘要
  const versionB = versionBId !== CURRENT_EDIT_ID ? currentVersions.find((v) => v.id === versionBId) : null;
  const versionA = versionAId !== CURRENT_EDIT_ID ? currentVersions.find((v) => v.id === versionAId) : null;
  const changeSummary = versionB?.change_summary || versionA?.change_summary;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('settings.prompt.version_comparison_title')}
      size="90%"
      styles={{ body: { padding: '0 24px 24px' } }}
    >
      <Stack gap="md">
        {/* Tabs */}
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tabs.List>
            <Tabs.Tab value="main">{t('settings.prompt.main_prompt_tab')}</Tabs.Tab>
            <Tabs.Tab value="realtime">{t('settings.prompt.realtime_prompt_tab')}</Tabs.Tab>
            <Tabs.Tab value="phone">{t('settings.prompt.phone_prompt_tab')}</Tabs.Tab>
          </Tabs.List>
        </Tabs>

        {loadingVersions && (
          <Group justify="center" py="md">
            <Loader size="sm" />
          </Group>
        )}

        {!loadingVersions && (
          <>
            {/* 版本选择器 */}
            <Group grow align="flex-end">
              <div>
                <Text size="xs" c="dimmed" mb={4}>
                  {t('settings.prompt.version_a')}
                </Text>
                <Select
                  data={versionOptions}
                  value={versionAId}
                  onChange={setVersionAId}
                  placeholder="Select version A"
                  size="sm"
                />
              </div>
              <div>
                <Text size="xs" c="dimmed" mb={4}>
                  {t('settings.prompt.version_b')}
                </Text>
                <Select
                  data={versionOptions}
                  value={versionBId}
                  onChange={setVersionBId}
                  placeholder="Select version B"
                  size="sm"
                />
              </div>
            </Group>

            {/* Diff 视图 */}
            {diff && diff.length > 0 && (
              <>
                <Group grow>
                  <Text size="sm" fw={600} ta="center" c="dimmed">
                    {t('settings.prompt.version_a')}
                  </Text>
                  <Text size="sm" fw={600} ta="center" c="dimmed">
                    {t('settings.prompt.version_b')}
                  </Text>
                </Group>
                <ScrollArea h={500} style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: 8 }}>
                  <div style={{ display: 'flex', minHeight: '100%' }}>
                    {/* 左侧 */}
                    <div style={{ flex: 1, borderRight: '1px solid var(--mantine-color-gray-3)' }}>
                      {diff.map((line, idx) => {
                        let bg = 'transparent';
                        if (line.type === 'removed') bg = '#fef2f2';
                        if (line.type === 'modified') bg = '#fefce8';
                        return (
                          <Box
                            key={idx}
                            px="xs"
                            py={2}
                            style={{
                              backgroundColor: bg,
                              fontFamily: 'monospace',
                              fontSize: 14,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              minHeight: 24,
                              display: 'flex',
                            }}
                          >
                            <Text
                              size="xs"
                              c="dimmed"
                              style={{
                                width: 36,
                                flexShrink: 0,
                                textAlign: 'right',
                                paddingRight: 8,
                                userSelect: 'none',
                              }}
                            >
                              {line.leftLineNo || ''}
                            </Text>
                            <span style={{ flex: 1 }}>
                              {line.leftSegments.length > 0 ? renderSegments(line.leftSegments) : '\u00A0'}
                            </span>
                          </Box>
                        );
                      })}
                    </div>
                    {/* 右侧 */}
                    <div style={{ flex: 1 }}>
                      {diff.map((line, idx) => {
                        let bg = 'transparent';
                        if (line.type === 'added') bg = '#f0fdf4';
                        if (line.type === 'modified') bg = '#fefce8';
                        return (
                          <Box
                            key={idx}
                            px="xs"
                            py={2}
                            style={{
                              backgroundColor: bg,
                              fontFamily: 'monospace',
                              fontSize: 14,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              minHeight: 24,
                              display: 'flex',
                            }}
                          >
                            <Text
                              size="xs"
                              c="dimmed"
                              style={{
                                width: 36,
                                flexShrink: 0,
                                textAlign: 'right',
                                paddingRight: 8,
                                userSelect: 'none',
                              }}
                            >
                              {line.rightLineNo || ''}
                            </Text>
                            <span style={{ flex: 1 }}>
                              {line.rightSegments.length > 0 ? renderSegments(line.rightSegments) : '\u00A0'}
                            </span>
                          </Box>
                        );
                      })}
                    </div>
                  </div>
                </ScrollArea>

                {/* 图例 */}
                <Group gap="lg">
                  <Group gap={4}>
                    <Box w={10} h={10} style={{ borderRadius: '50%', backgroundColor: '#fca5a5' }} />
                    <Text size="xs" c="dimmed">
                      {t('settings.prompt.legend_removed')}
                    </Text>
                  </Group>
                  <Group gap={4}>
                    <Box w={10} h={10} style={{ borderRadius: '50%', backgroundColor: '#86efac' }} />
                    <Text size="xs" c="dimmed">
                      {t('settings.prompt.legend_added')}
                    </Text>
                  </Group>
                  <Group gap={4}>
                    <Box w={10} h={10} style={{ borderRadius: '50%', backgroundColor: '#fde68a' }} />
                    <Text size="xs" c="dimmed">
                      {t('settings.prompt.legend_modified')}
                    </Text>
                  </Group>
                </Group>

                {/* AI 摘要 */}
                {changeSummary && (
                  <Box
                    p="md"
                    style={{
                      backgroundColor: 'var(--mantine-color-gray-0)',
                      borderRadius: 8,
                      border: '1px solid var(--mantine-color-gray-2)',
                    }}
                  >
                    <Group gap="xs" mb={4}>
                      <Text size="sm" fw={600}>
                        ✨ {t('settings.prompt.change_summary_title')}
                      </Text>
                      <Badge size="xs" variant="light">
                        {t('settings.prompt.ai_generated')}
                      </Badge>
                    </Group>
                    <Text size="sm" c="dimmed">
                      {changeSummary}
                    </Text>
                  </Box>
                )}
              </>
            )}

            {currentVersions.length === 0 && !loadingVersions && (
              <Text c="dimmed" ta="center" py="xl">
                {t('settings.prompt.no_versions')}
              </Text>
            )}
          </>
        )}

        {/* 底部操作 */}
        <Group justify="space-between">
          <Group gap="xs">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRestoreA}
              disabled={!versionAId || versionAId === CURRENT_EDIT_ID || operating}
            >
              {t('settings.prompt.restore_version_a')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRestoreB}
              disabled={!versionBId || versionBId === CURRENT_EDIT_ID || operating}
            >
              {t('settings.prompt.restore_version_b')}
            </Button>
          </Group>
          <Button variant="default" onClick={onClose}>
            {t('settings.prompt.cancel')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
